import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrl, getImageUrlSync } from '../../utils/imageUtils';

const ImageUpload = ({
  label,
  value,
  onChange,
  entityId,
  imageType,
  disabled = false,
  accept = 'image/*',
  maxSize = 10,
  className = ''
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value || null);
  const [error, setError] = useState(null);
  const [isNewUpload, setIsNewUpload] = useState(false);
  const [blobPreview, setBlobPreview] = useState(null); // Store the blob URL separately

  // Update preview when value prop changes (for editing existing questions with images)
  useEffect(() => {
    if (!value) {
      // Don't clear blob preview if we're in the middle of an upload
      if (!uploading && !blobPreview) {
        setPreviewUrl(null);
        setIsNewUpload(false);
        setBlobPreview(null);
      } else if (blobPreview) {
        setPreviewUrl(blobPreview);
      }
      return;
    }

    // If it's already a full URL (like a blob URL from file upload), use it directly
    if (value.startsWith('blob:') || value.startsWith('data:') || value.startsWith('http')) {
      setPreviewUrl(value);
      if (value.startsWith('blob:')) {
        setBlobPreview(value); // Store blob URL for later use
      }
      return;
    }

    // If this is a newly uploaded image and we have a blob preview, keep using it
    if (isNewUpload && blobPreview) {
      setPreviewUrl(blobPreview);
      // Still try to fetch the server image in the background
      setTimeout(() => {
        fetchImageUrl(value);
      }, 2000);
      return;
    }

    // For existing images, fetch immediately
    fetchImageUrl(value);
  }, [value, isNewUpload, blobPreview]);

  const fetchImageUrl = async (imagePath) => {
    try {
      // Store the current preview URL and blob preview in case we need to restore them
      const currentPreview = previewUrl;
      const currentBlobPreview = blobPreview;

      // For relative paths, first show a loading placeholder, then get the actual URL
      // But only if we don't already have a blob URL preview
      if (!currentBlobPreview && (!currentPreview || !currentPreview.startsWith('blob:'))) {
        setPreviewUrl(getImageUrlSync(imagePath));
      }

      // Async fetch the real URL
      const imageUrl = await getImageUrl(imagePath);
      if (imageUrl) {
        setPreviewUrl(imageUrl);
        setIsNewUpload(false); // Reset the flag once we successfully load the image
        setBlobPreview(null); // Clear blob preview since we have server image
      } else {
        // If we can't get the image URL and we have a blob preview, restore it
        if (currentBlobPreview) {
          setPreviewUrl(currentBlobPreview);
        } else if (currentPreview && currentPreview.startsWith('blob:')) {
          setPreviewUrl(currentPreview);
        } else {
          // Show a broken image placeholder only if we don't have a blob fallback
          setPreviewUrl(`data:image/svg+xml;base64,${btoa(`
            <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
              <rect width="100" height="100" fill="#f8f8f8" stroke="#ddd"/>
              <text x="50" y="45" text-anchor="middle" font-family="Arial" font-size="10" fill="#999">
                Image not
              </text>
              <text x="50" y="60" text-anchor="middle" font-family="Arial" font-size="10" fill="#999">
                available
              </text>
            </svg>
          `)}`);
        }
      }
    } catch (error) {
      console.warn('Error loading image preview:', error);
      // Don't set to null if we have a blob URL
      if (blobPreview) {
        setPreviewUrl(blobPreview);
      } else if (!previewUrl || !previewUrl.startsWith('blob:')) {
        setPreviewUrl(null);
      }
    }
  };

  const showToast = (message, type = 'info') => {
    if (type === 'success') {
      toast.success(message);
    } else if (type === 'error') {
      toast.error(message);
    } else {
      toast(message);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      const errorMsg = `Please select an image smaller than ${maxSize}MB`;
      setError(errorMsg);
      showToast(errorMsg, 'error');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      const errorMsg = 'Please select an image file';
      setError(errorMsg);
      showToast(errorMsg, 'error');
      return;
    }

    // Create preview and keep it during upload
    const reader = new FileReader();
    reader.onload = (e) => {
      const blobUrl = e.target?.result;
      setPreviewUrl(blobUrl);
      setBlobPreview(blobUrl); // Store the blob URL separately

      // Upload file but keep the blob preview until we confirm the upload is accessible
      uploadFile(file, blobUrl);
    };
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file, blobUrl) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', imageType);
      formData.append('entityId', entityId);
      formData.append('processImage', 'true');

      // Use the super admin API client which includes authentication headers
      const response = await superAdminAPI.uploadImage(formData);
      const result = response.data;

      if (result && result.success) {
        setIsNewUpload(true); // Mark this as a new upload
        onChange(result.imageUrl);
        showToast(`${label} uploaded successfully`, 'success');

        // Try to fetch the uploaded image after a short delay
        // If it fails, keep the blob URL as fallback
        setTimeout(async () => {
          try {
            const uploadedImageUrl = await getImageUrl(result.imageUrl);
            if (uploadedImageUrl) {
              setPreviewUrl(uploadedImageUrl);
              setIsNewUpload(false);
            }
            // Keep the current blob URL preview if server image is not available
          } catch (error) {
            console.warn('Could not fetch uploaded image, keeping blob preview:', error);
            // Keep the current blob URL preview - don't change anything
          }
        }, 2000);

      } else {
        throw new Error(result?.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
      setError(errorMessage);
      showToast(`Failed to upload ${label.toLowerCase()}: ${errorMessage}`, 'error');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!value) return;

    try {
      // Use the correct delete endpoint - the super-admin service expects /{imageUrl:.+}
      const response = await fetch(`/api/super-admin/images/${encodeURIComponent(value)}`, {
        method: 'DELETE',
        headers: {
          'X-User-ID': localStorage.getItem('userId') || 'super-admin'
        }
      });

      if (response.ok) {
        onChange(null);
        setPreviewUrl(null);
        showToast(`${label} removed successfully`, 'success');
      } else {
        console.warn(`Failed to delete image from storage: ${response.status}`);
        // Still clear the UI even if backend deletion fails
        onChange(null);
        setPreviewUrl(null);
        showToast(`${label} removed from question (storage cleanup may be needed)`, 'warning');
      }
    } catch (error) {
      console.error('Remove error:', error);
      // Still clear the UI even if backend deletion fails
      onChange(null);
      setPreviewUrl(null);
      showToast(`${label} removed from question (storage cleanup may be needed)`, 'warning');
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        {label}
      </label>
      
      <div className="border-2 border-dashed border-slate-300 rounded-lg hover:border-cyan-400 transition-colors">
        <div className="p-4">
          {previewUrl ? (
            <div className="space-y-3">
              <div className="relative">
                <img
                  src={previewUrl}
                  alt={label}
                  className="max-w-full max-h-48 object-contain rounded-md mx-auto"
                />
                {!disabled && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {!disabled && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleClick}
                    disabled={uploading}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin inline" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2 inline" />
                        Replace
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`flex flex-col items-center justify-center py-8 text-center ${
                disabled ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'
              }`}
              onClick={handleClick}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-8 w-8 mb-2 animate-spin text-slate-400" />
                  <p className="text-sm text-slate-600">Uploading...</p>
                </>
              ) : (
                <>
                  <ImageIcon className="h-8 w-8 mb-2 text-slate-400" />
                  <p className="text-sm text-slate-600">
                    Click to upload {label.toLowerCase()}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Max {maxSize}MB • JPG, PNG, GIF, WebP
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Show indicator when image exists but can't be previewed (fallback) */}
      {value && !previewUrl && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
            Image uploaded: {value.split('/').pop()}
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
};

export default ImageUpload;
